import React, { useState, useMemo } from 'react';
import { sans, mono, green, greenLight, greenDark, useTheme } from '../tokens';
import PANEL_CATALOG, { STATEMENT_TYPES, CATEGORIES } from '../data/panelCatalog';

const ST_COLORS = {
  balance_sheet: '#3B82F6',
  income_statement: '#10B981',
  cash_flow: '#8B5CF6',
  adjustments: '#F59E0B',
};

const PANEL_TYPES = [
  { id: 'generic', label: 'General' },
  { id: 'bank_reconciliation', label: 'Bank Reconciliation' },
  { id: 'aging_schedule', label: 'Aging Schedule' },
  { id: 'rollforward', label: 'Rollforward' },
  { id: 'amortization_schedule', label: 'Amortization Schedule' },
  { id: 'depreciation_schedule', label: 'Depreciation Schedule' },
  { id: 'payroll_reconciliation', label: 'Payroll Reconciliation' },
  { id: 'tax_reconciliation', label: 'Tax Reconciliation' },
  { id: 'debt_schedule', label: 'Debt Schedule' },
  { id: 'lease_schedule', label: 'Lease Schedule' },
  { id: 'fx_revaluation', label: 'FX Revaluation' },
  { id: 'intercompany', label: 'Intercompany' },
  { id: 'journal_entry', label: 'Journal Entry' },
  { id: 'variance_analysis', label: 'Variance Analysis' },
  { id: 'checklist', label: 'Checklist' },
  { id: 'investment_schedule', label: 'Investment Schedule' },
  { id: 'equity_rollforward', label: 'Equity Rollforward' },
  { id: 'cash_flow_indirect', label: 'Cash Flow (Indirect)' },
  { id: 'consolidation', label: 'Consolidation' },
];

export default function PanelConfigScreen({ activePanels, onTogglePanel, onCreatePanel, onRemovePanel, onClose }) {
  const t = useTheme();
  const [search, setSearch] = useState('');
  const [expandedSt, setExpandedSt] = useState({ balance_sheet: true, income_statement: true, cash_flow: true, adjustments: true });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPanel, setNewPanel] = useState({
    label: '',
    statementType: 'balance_sheet',
    category: 'current_assets',
    panelType: 'generic',
    glRange: '',
  });

  const activeSet = useMemo(() => new Set(activePanels || []), [activePanels]);

  // Filter panels by search
  const filteredPanels = useMemo(() => {
    if (!search.trim()) return PANEL_CATALOG;
    const q = search.toLowerCase();
    return PANEL_CATALOG.filter(p =>
      p.label.toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q) ||
      (p.glRange && p.glRange.includes(q))
    );
  }, [search]);

  const tree = useMemo(() => {
    const result = [];
    for (const st of STATEMENT_TYPES) {
      const cats = CATEGORIES.filter(c => c.statementType === st.id).sort((a, b) => a.sort - b.sort);
      const stNode = { ...st, categories: [] };
      for (const cat of cats) {
        const panels = filteredPanels
          .filter(p => p.statementType === st.id && p.category === cat.id)
          .sort((a, b) => a.sort - b.sort);
        if (panels.length > 0) {
          stNode.categories.push({ ...cat, panels });
        }
      }
      if (stNode.categories.length > 0) {
        result.push(stNode);
      }
    }
    return result;
  }, [filteredPanels]);

  const totalActive = activeSet.size;
  const totalPanels = PANEL_CATALOG.length;

  const activateAll = (panels) => {
    panels.forEach(p => { if (!activeSet.has(p.id)) onTogglePanel(p.id); });
  };

  const deactivateAll = (panels) => {
    panels.forEach(p => { if (activeSet.has(p.id)) onTogglePanel(p.id); });
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: t.overlay || 'rgba(0,0,0,0.4)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 680, maxHeight: '85vh', background: t.bgCard || '#fff',
        borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${t.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans }}>
                Panel Configuration
              </div>
              <div style={{ fontSize: 11, color: t.textFaint, marginTop: 2 }}>
                {totalActive} of {totalPanels} panels active
              </div>
            </div>
            <button onClick={onClose} style={{
              border: 'none', background: t.inputBg || '#F1F5F9', borderRadius: 6, width: 28, height: 28,
              cursor: 'pointer', fontSize: 14, color: t.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>&times;</button>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search panels..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 6,
              border: `1px solid ${t.border}`, background: t.inputBg || '#F8FAFC',
              fontSize: 12, fontFamily: sans, color: t.textPrimary,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Panel list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {tree.map(st => {
            const isExpanded = expandedSt[st.id] !== false;
            const stPanels = st.categories.flatMap(c => c.panels);
            const stActive = stPanels.filter(p => activeSet.has(p.id)).length;

            return (
              <div key={st.id}>
                {/* Statement type header */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 24px', cursor: 'pointer',
                  borderLeft: `3px solid ${ST_COLORS[st.id]}`,
                  background: t.bgSurface || '#F8FAFC',
                }} onClick={() => setExpandedSt(prev => ({ ...prev, [st.id]: !prev[st.id] }))}>
                  <span style={{
                    fontSize: 10, color: t.textMuted,
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s', display: 'inline-block',
                  }}>{'\u25B6'}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: ST_COLORS[st.id], fontFamily: sans, flex: 1 }}>
                    {st.label}
                  </span>
                  <span style={{ fontSize: 10, fontFamily: mono, color: t.textFaint }}>
                    {stActive}/{stPanels.length}
                  </span>
                  <button onClick={e => { e.stopPropagation(); activateAll(stPanels); }} style={{
                    border: `1px solid ${greenLight}`, background: greenLight + '40', borderRadius: 4,
                    padding: '2px 8px', fontSize: 9, color: greenDark, cursor: 'pointer', fontFamily: sans,
                  }}>All on</button>
                  <button onClick={e => { e.stopPropagation(); deactivateAll(stPanels); }} style={{
                    border: `1px solid ${t.border}`, background: 'transparent', borderRadius: 4,
                    padding: '2px 8px', fontSize: 9, color: t.textMuted, cursor: 'pointer', fontFamily: sans,
                  }}>All off</button>
                </div>

                {isExpanded && st.categories.map(cat => (
                  <div key={cat.id}>
                    {/* Category header */}
                    <div style={{
                      padding: '6px 24px 6px 36px',
                      fontSize: 10, fontWeight: 600, color: t.textSecondary,
                      textTransform: 'uppercase', letterSpacing: 0.3, fontFamily: sans,
                    }}>
                      {cat.label}
                    </div>

                    {/* Panels */}
                    {cat.panels.map(panel => {
                      const isActive = activeSet.has(panel.id);
                      return (
                        <div
                          key={panel.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '6px 24px 6px 48px',
                          }}
                        >
                          {/* Toggle */}
                          <div
                            onClick={() => onTogglePanel(panel.id)}
                            style={{
                              width: 32, height: 18, borderRadius: 9,
                              background: isActive ? green : '#CBD5E1',
                              cursor: 'pointer', position: 'relative',
                              transition: 'background 0.15s', flexShrink: 0,
                            }}
                          >
                            <div style={{
                              width: 14, height: 14, borderRadius: '50%',
                              background: '#fff', position: 'absolute', top: 2,
                              left: isActive ? 16 : 2,
                              transition: 'left 0.15s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            }} />
                          </div>

                          {/* Label */}
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: 12, fontFamily: sans,
                              color: isActive ? t.textPrimary : t.textMuted,
                              fontWeight: isActive ? 500 : 400,
                            }}>
                              {panel.label}
                            </div>
                          </div>

                          {/* GL range */}
                          {panel.glRange && (
                            <span style={{ fontSize: 9, fontFamily: mono, color: t.textFaint }}>
                              GL {panel.glRange}
                            </span>
                          )}

                          {/* Remove custom panel */}
                          {!panel.isSystem && onRemovePanel && (
                            <button
                              onClick={e => { e.stopPropagation(); onRemovePanel(panel.id); }}
                              title="Remove custom panel"
                              style={{
                                border: 'none', background: 'transparent', cursor: 'pointer',
                                fontSize: 14, color: t.textFaint, padding: '0 2px', lineHeight: 1,
                              }}
                            >&times;</button>
                          )}

                          {/* Panel type badge */}
                          <span style={{
                            fontSize: 8, padding: '1px 6px', borderRadius: 8,
                            background: isActive ? green + '12' : t.bgSurface || '#F1F5F9',
                            color: isActive ? green : t.textFaint,
                            fontFamily: mono, fontWeight: 500,
                          }}>
                            {panel.panelType}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Create custom panel form */}
        {showCreateForm && (
          <div style={{ padding: '16px 24px', borderTop: `1px solid ${t.border}`, background: greenLight + '20' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: sans, marginBottom: 12 }}>
              Create custom panel
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              {/* Name */}
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: t.textFaint, textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
                  Panel name
                </label>
                <input
                  type="text"
                  placeholder="e.g. SR&ED Credits"
                  value={newPanel.label}
                  onChange={e => setNewPanel({ ...newPanel, label: e.target.value })}
                  style={{
                    width: '100%', padding: '6px 10px', borderRadius: 6,
                    border: `1px solid ${t.border}`, background: t.bgCard || '#fff',
                    fontSize: 12, fontFamily: sans, color: t.textPrimary, outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* GL Range */}
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: t.textFaint, textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
                  GL account range
                </label>
                <input
                  type="text"
                  placeholder="e.g. 12600-12699"
                  value={newPanel.glRange}
                  onChange={e => setNewPanel({ ...newPanel, glRange: e.target.value })}
                  style={{
                    width: '100%', padding: '6px 10px', borderRadius: 6,
                    border: `1px solid ${t.border}`, background: t.bgCard || '#fff',
                    fontSize: 12, fontFamily: mono, color: t.textPrimary, outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Statement type */}
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: t.textFaint, textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
                  Statement type
                </label>
                <select
                  value={newPanel.statementType}
                  onChange={e => {
                    const st = e.target.value;
                    const firstCat = CATEGORIES.find(c => c.statementType === st);
                    setNewPanel({ ...newPanel, statementType: st, category: firstCat?.id || '' });
                  }}
                  style={{
                    width: '100%', padding: '6px 10px', borderRadius: 6,
                    border: `1px solid ${t.border}`, background: t.bgCard || '#fff',
                    fontSize: 12, fontFamily: sans, color: t.textPrimary, outline: 'none',
                    boxSizing: 'border-box',
                  }}
                >
                  {STATEMENT_TYPES.map(st => (
                    <option key={st.id} value={st.id}>{st.label}</option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: t.textFaint, textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
                  Category
                </label>
                <select
                  value={newPanel.category}
                  onChange={e => setNewPanel({ ...newPanel, category: e.target.value })}
                  style={{
                    width: '100%', padding: '6px 10px', borderRadius: 6,
                    border: `1px solid ${t.border}`, background: t.bgCard || '#fff',
                    fontSize: 12, fontFamily: sans, color: t.textPrimary, outline: 'none',
                    boxSizing: 'border-box',
                  }}
                >
                  {CATEGORIES.filter(c => c.statementType === newPanel.statementType).map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Panel type */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: t.textFaint, textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
                  Panel type (determines workspace behavior)
                </label>
                <select
                  value={newPanel.panelType}
                  onChange={e => setNewPanel({ ...newPanel, panelType: e.target.value })}
                  style={{
                    width: '100%', padding: '6px 10px', borderRadius: 6,
                    border: `1px solid ${t.border}`, background: t.bgCard || '#fff',
                    fontSize: 12, fontFamily: sans, color: t.textPrimary, outline: 'none',
                    boxSizing: 'border-box',
                  }}
                >
                  {PANEL_TYPES.map(pt => (
                    <option key={pt.id} value={pt.id}>{pt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowCreateForm(false); setNewPanel({ label: '', statementType: 'balance_sheet', category: 'current_assets', panelType: 'generic', glRange: '' }); }} style={{
                border: `1px solid ${t.border}`, background: 'transparent', borderRadius: 6,
                padding: '6px 14px', fontSize: 11, color: t.textMuted, cursor: 'pointer', fontFamily: sans,
              }}>Cancel</button>
              <button
                disabled={!newPanel.label.trim()}
                onClick={() => {
                  if (newPanel.label.trim() && onCreatePanel) {
                    onCreatePanel(newPanel);
                    setNewPanel({ label: '', statementType: 'balance_sheet', category: 'current_assets', panelType: 'generic', glRange: '' });
                    setShowCreateForm(false);
                  }
                }}
                style={{
                  border: 'none', background: newPanel.label.trim() ? green : '#CBD5E1', borderRadius: 6,
                  padding: '6px 14px', fontSize: 11, color: '#fff', cursor: newPanel.label.trim() ? 'pointer' : 'not-allowed',
                  fontFamily: sans, fontWeight: 600,
                }}
              >Create panel</button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '12px 24px', borderTop: `1px solid ${t.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          {!showCreateForm ? (
            <button onClick={() => setShowCreateForm(true)} style={{
              border: `1px solid ${green}40`, background: greenLight + '30', borderRadius: 6,
              padding: '6px 14px', fontSize: 11, color: greenDark, cursor: 'pointer', fontFamily: sans, fontWeight: 500,
            }}>+ Create custom panel</button>
          ) : (
            <span style={{ fontSize: 11, color: t.textFaint }}>
              Fill in the form above to create a new panel.
            </span>
          )}
          <button onClick={onClose} style={{
            border: 'none', background: green, borderRadius: 6,
            padding: '6px 20px', fontSize: 12, color: '#fff', cursor: 'pointer',
            fontFamily: sans, fontWeight: 600,
          }}>Done</button>
        </div>
      </div>
    </div>
  );
}
